import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, UserCircle2, Upload, Linkedin, Trophy, BadgeCheck } from "lucide-react";

const INITIAL = {
    photo_url: "",
    professional_title: "",
    specialties: [],
    qualifications: [],
    short_bio: "",
    long_bio: "",
    years_experience: 0,
    linkedin_url: "",
    sports_associations: [],
    professional_associations: [],
    display_associations_publicly: true,
};

// Module-scope small components to avoid react/no-unstable-nested-components
function SectionLabel({ children, required }) {
    return (
        <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-smoke">
            {children} {required && <span className="text-brand-orange">*</span>}
        </label>
    );
}

// Client-side resize/compression: max 600x600, JPEG ~85%. Returns a data URL.
async function fileToResizedDataUrl(file, maxSize = 600, quality = 0.85) {
    const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = () => rej(new Error("Lecture du fichier impossible."));
        r.readAsDataURL(file);
    });
    const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = () => rej(new Error("Image invalide."));
        i.src = dataUrl;
    });
    const { width, height } = img;
    const ratio = Math.min(1, maxSize / Math.max(width, height));
    const w = Math.round(width * ratio);
    const h = Math.round(height * ratio);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
}

export default function PublicProfileForm({ mode = "onboarding", onSubmitted }) {
    const [data, setData] = useState(INITIAL);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [specialtyInput, setSpecialtyInput] = useState("");
    const [qualInput, setQualInput] = useState({ name: "", year: "" });
    const [sportInput, setSportInput] = useState("");
    const [proInput, setProInput] = useState("");
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const fileInputRef = useRef(null);

    const onPickPhoto = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting same file
        if (!file) return;
        if (!/^image\//.test(file.type)) {
            toast.error("Merci de sélectionner une image (JPEG, PNG, WebP).");
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Image trop volumineuse (max 8 Mo).");
            return;
        }
        setUploadingPhoto(true);
        try {
            const resized = await fileToResizedDataUrl(file, 600, 0.85);
            setData((d) => ({ ...d, photo_url: resized }));
            toast.success("Photo prête. N'oubliez pas d'enregistrer.");
        } catch (err) {
            toast.error(err?.message || "Impossible de traiter l'image.");
        } finally {
            setUploadingPhoto(false);
        }
    };

    useEffect(() => {
        let alive = true;
        api.get("/formateur/profil-public")
            .then((r) => {
                if (!alive) return;
                setData({
                    photo_url: r.data.photo_url || "",
                    professional_title: r.data.professional_title || "",
                    specialties: r.data.specialties || [],
                    qualifications: r.data.qualifications || [],
                    short_bio: r.data.short_bio || "",
                    long_bio: r.data.long_bio || "",
                    years_experience: r.data.years_experience || 0,
                    linkedin_url: r.data.linkedin_url || "",
                    sports_associations: r.data.sports_associations || [],
                    professional_associations: r.data.professional_associations || [],
                    display_associations_publicly: r.data.display_associations_publicly !== false,
                });
            })
            .catch(() => {})
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    const addSpecialty = () => {
        const v = specialtyInput.trim();
        if (!v || data.specialties.includes(v) || data.specialties.length >= 10) return;
        setData((d) => ({ ...d, specialties: [...d.specialties, v] }));
        setSpecialtyInput("");
    };
    const removeSpecialty = (v) => setData((d) => ({ ...d, specialties: d.specialties.filter((x) => x !== v) }));

    const addAssoc = (kind) => {
        const v = (kind === "sport" ? sportInput : proInput).trim();
        if (!v) return;
        const key = kind === "sport" ? "sports_associations" : "professional_associations";
        if (data[key].includes(v) || data[key].length >= 15) return;
        setData((d) => ({ ...d, [key]: [...d[key], v] }));
        if (kind === "sport") setSportInput(""); else setProInput("");
    };
    const removeAssoc = (kind, v) => {
        const key = kind === "sport" ? "sports_associations" : "professional_associations";
        setData((d) => ({ ...d, [key]: d[key].filter((x) => x !== v) }));
    };

    const addQual = () => {
        const name = qualInput.name.trim();
        if (!name) return;
        const year = qualInput.year ? parseInt(qualInput.year, 10) : null;
        setData((d) => ({ ...d, qualifications: [...d.qualifications, { name, year }] }));
        setQualInput({ name: "", year: "" });
    };
    const removeQual = (i) => setData((d) => ({ ...d, qualifications: d.qualifications.filter((_, idx) => idx !== i) }));

    // Auto-commit pending inputs (typed but not "+ Add") so users are not blocked silently.
    const commitPendingInputs = () => {
        let next = { ...data };
        const s = specialtyInput.trim();
        if (s && !next.specialties.includes(s) && next.specialties.length < 10) {
            next = { ...next, specialties: [...next.specialties, s] };
            setSpecialtyInput("");
        }
        const qname = qualInput.name.trim();
        if (qname) {
            const y = qualInput.year ? parseInt(qualInput.year, 10) : null;
            next = { ...next, qualifications: [...next.qualifications, { name: qname, year: y }] };
            setQualInput({ name: "", year: "" });
        }
        const sp = sportInput.trim();
        if (sp && !next.sports_associations.includes(sp) && next.sports_associations.length < 15) {
            next = { ...next, sports_associations: [...next.sports_associations, sp] };
            setSportInput("");
        }
        const pr = proInput.trim();
        if (pr && !next.professional_associations.includes(pr) && next.professional_associations.length < 15) {
            next = { ...next, professional_associations: [...next.professional_associations, pr] };
            setProInput("");
        }
        if (next !== data) setData(next);
        return next;
    };

    // Explicit missing-fields list (used for both visual state and error toast).
    const getMissingFields = (d) => {
        const miss = [];
        if (!d.photo_url.trim()) miss.push("photo de profil");
        if (!d.professional_title.trim()) miss.push("titre professionnel");
        if (d.specialties.length === 0) miss.push("au moins 1 spécialité (cliquez sur « + Ajouter »)");
        if (d.qualifications.length === 0) miss.push("au moins 1 qualification (cliquez sur « + Ajouter »)");
        if (!d.short_bio.trim()) miss.push("bio courte");
        return miss;
    };

    const missing = getMissingFields(data);

    const submit = async (e) => {
        e.preventDefault();
        // Best-effort: commit anything typed but not yet added.
        const committed = commitPendingInputs();
        const missingNow = getMissingFields(committed);
        if (missingNow.length > 0) {
            toast.error(
                `Il manque : ${missingNow.join(" · ")}.`,
                { duration: 7000 }
            );
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...committed,
                years_experience: parseInt(committed.years_experience || 0, 10) || 0,
                qualifications: committed.qualifications.map((q) => ({
                    name: q.name,
                    year: q.year ? parseInt(q.year, 10) : null,
                })),
            };
            await api.put("/formateur/profil-public", payload);
            toast.success("Profil public enregistré.");
            if (onSubmitted) onSubmitted();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-8 text-sm text-brand-smoke">Chargement du profil…</div>;

    return (
        <form onSubmit={submit} className="mx-auto max-w-3xl space-y-8" data-testid="public-profile-form">
            {mode === "onboarding" && (
                <div className="rounded-2xl border border-brand-line bg-[#FAFAFA] p-5 text-sm text-brand-ink/80">
                    <div className="font-display text-lg text-brand-ink">Votre profil public</div>
                    <p className="mt-1 text-sm text-brand-ink/70">
                        Ces informations seront visibles par vos futurs stagiaires sur les pages de formations et de cursus.
                        Soignez-les : elles sont votre vitrine.
                    </p>
                </div>
            )}

            {/* Photo */}
            <div>
                <SectionLabel required>Photo de profil</SectionLabel>
                <div className="mt-3 flex items-start gap-5">
                    <div className="flex h-28 w-28 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-brand-line bg-brand-sand">
                        {data.photo_url ? (
                            <img src={data.photo_url} alt="Aperçu" className="h-full w-full object-cover" />
                        ) : (
                            <UserCircle2 className="h-10 w-10 text-brand-smoke" />
                        )}
                    </div>
                    <div className="flex-1">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={onPickPhoto}
                            className="hidden"
                            data-testid="profile-photo-input"
                        />
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingPhoto}
                                data-testid="profile-photo-pick"
                                className="inline-flex items-center gap-2 rounded-xl border border-brand-line bg-white px-4 py-2.5 text-sm font-semibold text-brand-ink transition-colors hover:border-brand-orange hover:text-brand-orange disabled:opacity-60"
                            >
                                <Upload className="h-4 w-4" />
                                {uploadingPhoto
                                    ? "Traitement…"
                                    : data.photo_url
                                      ? "Changer la photo"
                                      : "Choisir une photo"}
                            </button>
                            {data.photo_url && (
                                <button
                                    type="button"
                                    onClick={() => setData((d) => ({ ...d, photo_url: "" }))}
                                    data-testid="profile-photo-clear"
                                    className="inline-flex items-center gap-2 rounded-xl border border-brand-line bg-white px-4 py-2.5 text-sm text-brand-smoke transition-colors hover:border-red-300 hover:text-red-600"
                                >
                                    <Trash2 className="h-4 w-4" /> Retirer
                                </button>
                            )}
                        </div>
                        <p className="mt-2 text-xs text-brand-smoke">
                            JPEG, PNG ou WebP · l&apos;image sera automatiquement recadrée à 600×600 px et
                            optimisée avant enregistrement.
                        </p>
                    </div>
                </div>
            </div>

            {/* Professional title */}
            <div>
                <SectionLabel required>Titre professionnel</SectionLabel>
                <input
                    type="text"
                    value={data.professional_title}
                    onChange={(e) => setData((d) => ({ ...d, professional_title: e.target.value }))}
                    placeholder="Ex : Masseur-kinésithérapeute D.E., spécialisé en thérapie manuelle"
                    required
                    data-testid="profile-professional-title"
                    className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                />
            </div>

            {/* Specialties */}
            <div>
                <SectionLabel required>Spécialités</SectionLabel>
                <p className="mt-1 text-xs text-brand-smoke">Ajoutez vos axes cliniques (Entrée ↵ ou bouton +).</p>
                <div className="mt-3 flex gap-2">
                    <input
                        type="text"
                        value={specialtyInput}
                        onChange={(e) => setSpecialtyInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                addSpecialty();
                            }
                        }}
                        placeholder="Ex : Thérapie manuelle"
                        data-testid="profile-specialty-input"
                        className="flex-1 rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                    />
                    <button
                        type="button"
                        onClick={addSpecialty}
                        disabled={!specialtyInput.trim()}
                        data-testid="profile-specialty-add"
                        className="inline-flex items-center gap-1 rounded-xl bg-brand-ink px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
                    >
                        <Plus className="h-4 w-4" /> Ajouter
                    </button>
                </div>
                {data.specialties.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {data.specialties.map((s) => (
                            <span
                                key={s}
                                className="inline-flex items-center gap-2 rounded-full bg-brand-orange/10 px-3 py-1 text-xs font-semibold text-brand-orange"
                                data-testid={`profile-specialty-tag-${s}`}
                            >
                                {s}
                                <button
                                    type="button"
                                    onClick={() => removeSpecialty(s)}
                                    aria-label="Retirer"
                                    className="hover:text-brand-ink"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Qualifications */}
            <div>
                <SectionLabel required>Qualifications / diplômes</SectionLabel>
                <p className="mt-1 text-xs text-brand-smoke">Ajoutez vos diplômes et certifications.</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_120px_auto]">
                    <input
                        type="text"
                        value={qualInput.name}
                        onChange={(e) => setQualInput((q) => ({ ...q, name: e.target.value }))}
                        placeholder="Nom du diplôme / certification"
                        data-testid="profile-qual-name"
                        className="rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                    />
                    <input
                        type="number"
                        min="1950"
                        max="2100"
                        value={qualInput.year}
                        onChange={(e) => setQualInput((q) => ({ ...q, year: e.target.value }))}
                        placeholder="Année"
                        data-testid="profile-qual-year"
                        className="rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                    />
                    <button
                        type="button"
                        onClick={addQual}
                        disabled={!qualInput.name.trim()}
                        data-testid="profile-qual-add"
                        className="inline-flex items-center justify-center gap-1 rounded-xl bg-brand-ink px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
                    >
                        <Plus className="h-4 w-4" /> Ajouter
                    </button>
                </div>
                {data.qualifications.length > 0 && (
                    <ul className="mt-4 space-y-2">
                        {data.qualifications.map((q, i) => (
                            <li
                                key={`${q.name}-${i}`}
                                className="flex items-center justify-between rounded-xl border border-brand-line bg-white px-4 py-2.5 text-sm"
                                data-testid={`profile-qual-row-${i}`}
                            >
                                <span className="text-brand-ink">
                                    <strong>{q.name}</strong>
                                    {q.year ? <span className="ml-2 text-brand-smoke">— {q.year}</span> : null}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeQual(i)}
                                    className="text-brand-smoke hover:text-red-600"
                                    aria-label="Supprimer"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Short bio */}
            <div>
                <SectionLabel required>Bio courte (500 caractères max)</SectionLabel>
                <textarea
                    value={data.short_bio}
                    onChange={(e) => setData((d) => ({ ...d, short_bio: e.target.value }))}
                    rows={4}
                    maxLength={500}
                    required
                    placeholder="3 à 5 phrases : votre parcours, votre approche, ce qui vous distingue."
                    data-testid="profile-short-bio"
                    className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                />
                <div className="mt-1 text-right text-xs text-brand-smoke">{data.short_bio.length}/500</div>
            </div>

            {/* Long bio */}
            <div>
                <SectionLabel>Bio longue (optionnelle, 2 000 caractères max)</SectionLabel>
                <textarea
                    value={data.long_bio}
                    onChange={(e) => setData((d) => ({ ...d, long_bio: e.target.value }))}
                    rows={6}
                    maxLength={2000}
                    placeholder="Parcours détaillé, publications, expériences marquantes…"
                    data-testid="profile-long-bio"
                    className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                />
                <div className="mt-1 text-right text-xs text-brand-smoke">{data.long_bio.length}/2000</div>
            </div>

            {/* Years & LinkedIn */}
            <div className="grid gap-5 sm:grid-cols-2">
                <div>
                    <SectionLabel>Années d&apos;expérience</SectionLabel>
                    <input
                        type="number"
                        min="0"
                        max="70"
                        value={data.years_experience}
                        onChange={(e) => setData((d) => ({ ...d, years_experience: e.target.value }))}
                        data-testid="profile-years"
                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                    />
                </div>
                <div>
                    <SectionLabel>Lien LinkedIn (optionnel)</SectionLabel>
                    <div className="relative mt-2">
                        <Linkedin className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                        <input
                            type="url"
                            value={data.linkedin_url}
                            onChange={(e) => setData((d) => ({ ...d, linkedin_url: e.target.value }))}
                            placeholder="https://www.linkedin.com/in/…"
                            data-testid="profile-linkedin"
                            className="w-full rounded-xl border border-brand-line py-3 pl-11 pr-4 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                        />
                    </div>
                </div>
            </div>

            {/* Associations sportives */}
            <div>
                <SectionLabel>Associations sportives</SectionLabel>
                <p className="mt-1 text-xs text-brand-smoke">
                    Clubs ou fédérations sportives avec lesquels vous collaborez (kiné du sport, équipes, fédérations).
                </p>
                <div className="mt-3 flex gap-2">
                    <input
                        type="text"
                        value={sportInput}
                        onChange={(e) => setSportInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                addAssoc("sport");
                            }
                        }}
                        placeholder="Ex : Stade Toulousain Rugby"
                        data-testid="profile-sport-assoc-input"
                        className="flex-1 rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                    />
                    <button
                        type="button"
                        onClick={() => addAssoc("sport")}
                        disabled={!sportInput.trim()}
                        data-testid="profile-sport-assoc-add"
                        className="inline-flex items-center gap-1 rounded-xl bg-brand-ink px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
                    >
                        <Plus className="h-4 w-4" /> Ajouter
                    </button>
                </div>
                {data.sports_associations.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {data.sports_associations.map((s) => (
                            <span
                                key={s}
                                className="inline-flex items-center gap-2 rounded-full border border-brand-orange/30 bg-brand-orange/5 px-3 py-1 text-xs font-medium text-brand-ink"
                                data-testid={`profile-sport-assoc-tag-${s}`}
                            >
                                <Trophy className="h-3.5 w-3.5 text-brand-orange" />
                                {s}
                                <button
                                    type="button"
                                    onClick={() => removeAssoc("sport", s)}
                                    aria-label="Retirer"
                                    className="hover:text-red-600"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Associations professionnelles */}
            <div>
                <SectionLabel>Associations professionnelles</SectionLabel>
                <p className="mt-1 text-xs text-brand-smoke">
                    Sociétés savantes, syndicats, ordres professionnels (SFMK, CNOMK, etc.).
                </p>
                <div className="mt-3 flex gap-2">
                    <input
                        type="text"
                        value={proInput}
                        onChange={(e) => setProInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                addAssoc("pro");
                            }
                        }}
                        placeholder="Ex : SFMK — Société Française de Masso-Kinésithérapie"
                        data-testid="profile-pro-assoc-input"
                        className="flex-1 rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                    />
                    <button
                        type="button"
                        onClick={() => addAssoc("pro")}
                        disabled={!proInput.trim()}
                        data-testid="profile-pro-assoc-add"
                        className="inline-flex items-center gap-1 rounded-xl bg-brand-ink px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
                    >
                        <Plus className="h-4 w-4" /> Ajouter
                    </button>
                </div>
                {data.professional_associations.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {data.professional_associations.map((s) => (
                            <span
                                key={s}
                                className="inline-flex items-center gap-2 rounded-full border border-brand-ink/15 bg-white px-3 py-1 text-xs font-medium text-brand-ink"
                                data-testid={`profile-pro-assoc-tag-${s}`}
                            >
                                <BadgeCheck className="h-3.5 w-3.5 text-brand-orange" />
                                {s}
                                <button
                                    type="button"
                                    onClick={() => removeAssoc("pro", s)}
                                    aria-label="Retirer"
                                    className="hover:text-red-600"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {/* Visibility toggle */}
                <label
                    className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-brand-line bg-[#FAFAFA] p-4 text-sm"
                    data-testid="profile-display-assoc-toggle-label"
                >
                    <input
                        type="checkbox"
                        checked={data.display_associations_publicly}
                        onChange={(e) =>
                            setData((d) => ({ ...d, display_associations_publicly: e.target.checked }))
                        }
                        data-testid="profile-display-assoc-toggle"
                        className="mt-0.5 h-4 w-4 accent-brand-orange"
                    />
                    <span className="text-brand-ink/80">
                        <strong className="text-brand-ink">Afficher mes associations publiquement</strong>
                        <span className="mt-0.5 block text-xs text-brand-smoke">
                            Si décoché, vos associations resteront visibles uniquement par l&apos;administration.
                        </span>
                    </span>
                </label>
            </div>

            <div className="pt-2">
                {missing.length > 0 && (
                    <div
                        className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                        data-testid="profile-missing-hint"
                    >
                        <strong className="font-semibold">Pour continuer, il vous reste à renseigner :</strong>
                        <ul className="mt-1.5 list-disc pl-5 space-y-0.5">
                            {missing.map((m) => (
                                <li key={m}>{m}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="profile-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#d9772c] disabled:opacity-50"
                >
                    {submitting ? "Enregistrement…" : mode === "onboarding" ? "Enregistrer et continuer" : "Enregistrer mon profil"}
                </button>
            </div>
        </form>
    );
}
